import { useState } from 'react';
import AuthContext from './AuthContext';
import api from '../../services/api';

const LOGIN_URL = '/auth';
const CREATE_ACCOUNT_URL = '/create';
const GOOGLE_LOGIN_URL = '/google';

const authenticationAPI = {
    isAuthenticated: false,
    async signin(user: string, password: string) {
        const authenticationResponse = await api.post(
            LOGIN_URL,
            JSON.stringify({
                username: user,
                password,
            }),
            {
                headers: {
                    'Content-Type': 'application/json',
                    withCredentials: true,
                },
            },
        );
        const accessToken = authenticationResponse?.data?.accessToken;
        const roles = authenticationResponse?.data?.roles;
        const orgs = authenticationResponse?.data?.orgs;
        const id = authenticationResponse?.data?.id;
        const username = authenticationResponse?.data?.username;
        const firstName = authenticationResponse?.data?.firstName;
        const lastName = authenticationResponse?.data?.lastName;
        const email = authenticationResponse?.data?.email;

        return {
            accessToken,
            roles,
            orgs,
            id,
            username,
            firstName,
            lastName,
            email,
        };
    },
    async signinGoogle(jwt: string) {
        const authenticationResponse = await api.post(
            GOOGLE_LOGIN_URL,
            JSON.stringify({ credential: jwt }),
            {
                headers: {
                    'Content-Type': 'application/json',
                    withCredentials: true,
                },
            },
        );
        const accessToken = authenticationResponse?.data?.accessToken;
        const username = authenticationResponse?.data?.username;
        const roles = authenticationResponse?.data?.roles;
        const orgs = authenticationResponse?.data?.orgs;
        const id = authenticationResponse?.data?.id;
        const firstName = authenticationResponse?.data?.firstName;
        const lastName = authenticationResponse?.data?.lastName;
        const email = authenticationResponse?.data?.email;

        return {
            accessToken,
            roles,
            username,
            orgs,
            id,
            firstName,
            lastName,
            email,
        };
    },
    async signout() {
        authenticationAPI.isAuthenticated = false;
        return authenticationAPI.isAuthenticated;
    },
    async createAccount(
        newEmail: string,
        password: string,
        newFirstName: string,
        newLastName: string,
        organizationName: string,
        logoUrl: string,
        selectedWork: string,
        selectedSource: string,
    ) {
        try {
            const authenticationResponse = await api.post(
                CREATE_ACCOUNT_URL,
                JSON.stringify({
                    email: newEmail,
                    password,
                    firstName: newFirstName,
                    lastName: newLastName,
                    organizationName,
                    logoUrl,
                    selectedWork,
                    selectedSource,
                }),
                {
                    headers: {
                        'Content-Type': 'application/json',
                        withCredentials: true,
                    },
                },
            );

            const accessToken = authenticationResponse?.data?.accessToken;
            const roles = authenticationResponse?.data?.roles;
            const orgs = authenticationResponse?.data?.orgs;
            const id = authenticationResponse?.data?.id;
            const username = authenticationResponse?.data?.username;
            const firstName = authenticationResponse?.data?.firstName;
            const lastName = authenticationResponse?.data?.lastName;
            const email = authenticationResponse?.data?.email;

            return {
                accessToken,
                roles,
                orgs,
                id,
                username,
                firstName,
                lastName,
                email,
            };
        } catch (error) {
            console.error('Error creating account', error);
            throw error;
        }
    },
};

export default function AuthProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [user, setUser] = useState<any>(null);
    const [persist, setPersist] = useState<boolean>(
        // @ts-ignore
        JSON.parse(localStorage.getItem('persist')) || true,
    );

    const signin = async (username: string, password: string) => {
        const authenticationResponse = await authenticationAPI.signin(
            username,
            password,
        );

        if (authenticationResponse.accessToken) {
            const {
                accessToken,
                roles,
                orgs,
                id,
                username,
                firstName,
                lastName,
                email,
            } = authenticationResponse;

            setUser({
                username,
                password,
                accessToken,
                roles,
                orgs,
                id,
                firstName,
                lastName,
                email,
            });
        }

        return !!authenticationResponse.accessToken;
    };

    const createAccountAndLogin = async (
        email: string,
        password: string,
        firstName: string,
        lastName: string,
        organizationName: string,
        logoUrl: string,
        selectedWork: string,
        selectedSource: string,
    ) => {
        const authenticationResponse = await authenticationAPI.createAccount(
            email,
            password,
            firstName,
            lastName,
            organizationName,
            logoUrl,
            selectedWork,
            selectedSource,
        );

        if (authenticationResponse.accessToken) {
            const {
                accessToken,
                roles,
                orgs,
                id,
                username,
                firstName,
                lastName,
                email,
            } = authenticationResponse;

            setUser({
                username,
                password,
                accessToken,
                roles,
                orgs,
                id,
                firstName,
                lastName,
                email,
            });
        }

        return !!authenticationResponse.accessToken;
    };

    const signInGoogle = async (jwt: string) => {
        const authenticationResponse = await authenticationAPI.signinGoogle(
            jwt,
        );

        localStorage.setItem('persist', JSON.stringify(true));
        setPersist(true);

        if (authenticationResponse.accessToken) {
            const {
                accessToken,
                roles,
                orgs,
                id,
                username,
                firstName,
                lastName,
                email,
            } = authenticationResponse;
            setUser({
                username,
                password: '',
                accessToken,
                roles,
                orgs,
                id,
                firstName,
                lastName,
                email,
            });
        }

        return !!authenticationResponse.accessToken;
    };

    const signout = async () => {
        await authenticationAPI.signout();
        setUser(null);
    };

    const authValue = {
        auth: user,
        setAuth: setUser,
        signin,
        signout,
        persist,
        setPersist,
        signInGoogle,
        createAccountAndLogin,
    };

    return (
        <AuthContext.Provider value={authValue}>
            {children}
        </AuthContext.Provider>
    );
}
